import { Zlib } from 'fflate';
import { buildAIGCPayloadString } from '../metadata';

// Pre-computed CRC32 table for PNG
const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) {
      if (c & 1) {
        c = 0xedb88320 ^ (c >>> 1);
      } else {
        c = c >>> 1;
      }
    }
    table[i] = c;
  }
  return table;
})();

// Calculate CRC32 for type + data without array concatenation
function chunkCrc32(type, data) {
  let crc = 0xffffffff;
  // Process type bytes
  for (let i = 0; i < type.length; i++) {
    crc = crcTable[(crc ^ type[i]) & 0xff] ^ (crc >>> 8);
  }
  // Process data bytes if present
  if (data) {
    for (let i = 0; i < data.length; i++) {
      crc = crcTable[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function u32be(value) {
  return new Uint8Array([
    (value >>> 24) & 255,
    (value >>> 16) & 255,
    (value >>> 8) & 255,
    value & 255,
  ]);
}

function strToU8(str) {
  const out = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) out[i] = str.charCodeAt(i);
  return out;
}

function writeChunk(outParts, type, data) {
  const typeBytes = strToU8(type);
  const length = data ? data.length : 0;
  outParts.push(u32be(length));
  outParts.push(typeBytes);
  if (length) outParts.push(data);
  const crc = chunkCrc32(typeBytes, data);
  outParts.push(u32be(crc));
}

function buildITXtChunkData(keyword, text) {
  const keyBytes = strToU8(keyword);
  const textBytes = strToU8(text);
  // iTXt layout:
  // keyword\0, compression-flag (0), compression-method (0), language-tag\0, translated-keyword\0, text (UTF-8)
  const data = new Uint8Array(keyBytes.length + 1 + 1 + 1 + 1 + 1 + textBytes.length);
  let offset = 0;
  data.set(keyBytes, offset);
  offset += keyBytes.length;
  data[offset++] = 0; // keyword null terminator
  data[offset++] = 0; // compression flag = 0 (uncompressed)
  data[offset++] = 0; // compression method = 0
  data[offset++] = 0; // empty language tag terminator
  data[offset++] = 0; // empty translated keyword terminator
  data.set(textBytes, offset);
  return data;
}

export function createPNGStreamEncoder(width, height, { level = 6, metadata } = {}) {
  const outParts = [];

  // PNG signature
  outParts.push(new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]));

  // IHDR chunk
  const ihdr = new Uint8Array(13);
  ihdr.set(u32be(width), 0);
  ihdr.set(u32be(height), 4);
  ihdr[8] = 8; // bit depth
  ihdr[9] = 6; // color type RGBA
  ihdr[10] = 0; // compression method
  ihdr[11] = 0; // filter method
  ihdr[12] = 0; // interlace method
  writeChunk(outParts, 'IHDR', ihdr);

  // Optional AIGC metadata via iTXt chunk
  if (metadata) {
    try {
      const text = buildAIGCPayloadString(metadata);
      const itxt = buildITXtChunkData('AIGC', text);
      writeChunk(outParts, 'iTXt', itxt);
    } catch {
      /* ignore metadata errors */
    }
  }

  // Zlib compressor for IDAT (PNG spec requires zlib format)
  const idatChunks = [];
  const deflater = new Zlib(
    (chunk, final) => {
      if (chunk.length) idatChunks.push(chunk);
      if (final) {
        // Combine all deflated data into a single IDAT chunk
        const totalLength = idatChunks.reduce((sum, c) => sum + c.length, 0);
        const combinedData = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of idatChunks) {
          combinedData.set(chunk, offset);
          offset += chunk.length;
        }
        writeChunk(outParts, 'IDAT', combinedData);
      }
    },
    { level }
  );

  const rowBuffer = new Uint8Array(width * 4 + 1);
  let prevRow = null;

  // Filter selection helpers
  function calculateFilterCost(filteredRow) {
    // Simple heuristic: sum of absolute values (smaller is better for compression)
    let cost = 0;
    for (let i = 1; i < filteredRow.length; i++) {
      const val = filteredRow[i];
      cost += val < 128 ? val : 256 - val;
    }
    return cost;
  }

  function writeRow(rgbaRow) {
    // rgbaRow length must be width*4

    let bestCost = Infinity;
    const tempBuffer = new Uint8Array(width * 4 + 1);

    // Try None filter
    tempBuffer[0] = 0;
    tempBuffer.set(rgbaRow, 1);
    let cost = calculateFilterCost(tempBuffer);
    if (cost < bestCost) {
      bestCost = cost;
      rowBuffer.set(tempBuffer);
    }

    // Try Sub filter (predict from left pixel)
    tempBuffer[0] = 1;
    for (let i = 0; i < width * 4; i++) {
      const left = i >= 4 ? rgbaRow[i - 4] : 0;
      tempBuffer[i + 1] = (rgbaRow[i] - left + 256) & 255;
    }
    cost = calculateFilterCost(tempBuffer);
    if (cost < bestCost) {
      bestCost = cost;
      rowBuffer.set(tempBuffer);
    }

    // Try Up filter (predict from above pixel) if we have a previous row
    if (prevRow) {
      tempBuffer[0] = 2;
      for (let i = 0; i < width * 4; i++) {
        tempBuffer[i + 1] = (rgbaRow[i] - prevRow[i] + 256) & 255;
      }
      cost = calculateFilterCost(tempBuffer);
      if (cost < bestCost) {
        bestCost = cost;
        rowBuffer.set(tempBuffer);
      }
    }

    deflater.push(rowBuffer);

    // Update previous row
    if (!prevRow) prevRow = new Uint8Array(width * 4);
    prevRow.set(rgbaRow);
  }

  function end() {
    // finalize zlib stream and write IEND
    deflater.push(new Uint8Array(0), true);
    writeChunk(outParts, 'IEND', null);
    return new Blob(outParts, { type: 'image/png' });
  }

  return { writeRow, end };
}
